import React, { useMemo, useState } from 'react';

function formatDateTime(dateString) {
  if (!dateString) {
    return 'Not yet recorded';
  }

  return new Date(dateString).toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDate(dateString) {
  if (!dateString) {
    return 'Not set';
  }

  return new Date(`${dateString}T12:00:00`).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function getStatusMeta(status) {
  if (status === 'picked_up') {
    return { label: 'Picked up', tone: 'low' };
  }

  if (status === 'returned') {
    return { label: 'Returned', tone: 'neutral' };
  }

  return { label: 'Valid', tone: 'available' };
}

export default function EquipmentVerificationPanel({
  equipmentById,
  locationById,
  onVerify,
  onPickup,
  onReturn,
}) {
  const [query, setQuery] = useState('');
  const [reservation, setReservation] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const statusMeta = useMemo(
    () => getStatusMeta(reservation?.verificationStatus),
    [reservation?.verificationStatus],
  );

  const handleVerify = async () => {
    const trimmed = query.trim();
    if (!trimmed) {
      setError('Enter a reservation ID or QR payload to continue.');
      return;
    }

    setBusy(true);
    setError('');

    try {
      const nextReservation = await onVerify(trimmed);
      if (!nextReservation) {
        setReservation(null);
        setError('No reservation matched that code.');
      } else {
        setReservation(nextReservation);
      }
    } catch (verifyError) {
      setReservation(null);
      setError(verifyError?.message || 'We could not verify that reservation.');
    } finally {
      setBusy(false);
    }
  };

  const handlePickup = async () => {
    if (!reservation) {
      return;
    }

    setBusy(true);
    setError('');

    try {
      const updated = await onPickup(reservation.id);
      setReservation(updated);
    } catch (pickupError) {
      setError(pickupError?.message || 'Pickup could not be recorded.');
    } finally {
      setBusy(false);
    }
  };

  const handleReturn = async () => {
    if (!reservation) {
      return;
    }

    setBusy(true);
    setError('');

    try {
      const updated = await onReturn(reservation.id);
      setReservation(updated);
    } catch (returnError) {
      setError(returnError?.message || 'Return could not be recorded.');
    } finally {
      setBusy(false);
    }
  };

  const equipment = reservation ? equipmentById[reservation.equipmentId] : null;
  const location = reservation ? locationById[reservation.locationId] : null;

  return (
    <div className="equipment-library__section-card">
      <div className="equipment-library__section-heading">
        <div>
          <div className="equipment-library__eyebrow">Staff verification</div>
          <h3>Verify holds, pickups, and returns</h3>
        </div>
      </div>

      <p className="equipment-library__section-copy">
        Staff can paste a QR payload or type the reservation ID to confirm status and update inventory.
      </p>

      <div className="equipment-library__verify-row">
        <label className="equipment-library__field equipment-library__field--grow">
          <span className="equipment-library__visually-hidden">Reservation ID or QR code</span>
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Example: ELL-20260508-ABCDE"
            aria-label="Reservation ID or QR code"
          />
        </label>
        <button
          type="button"
          className="equipment-library__primary-button"
          onClick={handleVerify}
          disabled={busy}
        >
          {busy ? 'Checking...' : 'Verify reservation'}
        </button>
      </div>

      {error && <div className="equipment-library__error-banner">{error}</div>}

      {reservation ? (
        <div className="equipment-library__verification-result">
          <div className="equipment-library__verification-header">
            <div>
              <div className={`equipment-library__status-badge equipment-library__status-badge--${statusMeta.tone}`}>
                {statusMeta.label}
              </div>
              <h4>{reservation.id}</h4>
            </div>
            <p>{equipment?.name || reservation.equipmentId}</p>
          </div>

          <div className="equipment-library__verification-grid">
            <div className="equipment-library__detail-row">
              <span>Borrower</span>
              <strong>{reservation.borrowerName}</strong>
            </div>
            <div className="equipment-library__detail-row">
              <span>Pickup location</span>
              <strong>{location?.name || reservation.locationId}</strong>
            </div>
            <div className="equipment-library__detail-row">
              <span>Preferred pickup date</span>
              <strong>{formatDate(reservation.preferredPickupDate)}</strong>
            </div>
            <div className="equipment-library__detail-row">
              <span>Created</span>
              <strong>{formatDateTime(reservation.createdAt)}</strong>
            </div>
            <div className="equipment-library__detail-row">
              <span>Picked up</span>
              <strong>{formatDateTime(reservation.pickedUpAt)}</strong>
            </div>
            <div className="equipment-library__detail-row">
              <span>Returned</span>
              <strong>{formatDateTime(reservation.returnedAt)}</strong>
            </div>
          </div>

          <div className="equipment-library__modal-actions">
            <button
              type="button"
              className="equipment-library__secondary-button"
              onClick={handlePickup}
              disabled={busy || reservation.status !== 'held'}
            >
              Mark picked up
            </button>
            <button
              type="button"
              className="equipment-library__primary-button"
              onClick={handleReturn}
              disabled={busy || reservation.status !== 'picked_up'}
            >
              Mark returned
            </button>
          </div>
        </div>
      ) : (
        <div className="equipment-library__empty-state">
          <strong>No reservation selected</strong>
          <p>Verification details will appear here after a valid reservation lookup.</p>
        </div>
      )}
    </div>
  );
}
